import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { SPINNER_MOTION } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

interface ChatContextLimitNotificationProps {
  // Whether this message belongs to a subagent conversation. Subagents can't
  // start their own top-level conversation, so the buttons are hidden for them.
  isSubagent?: boolean;
  onCreateConversation?: () => void;
  // Triggers an async backend job: a large model summarizes the current
  // conversation and seeds a brand-new conversation with that summary so the
  // user can keep going without losing context. Hidden for subagents.
  onCompactConversation?: () => void;
}

export const ChatContextLimitNotification: React.FC<
  ChatContextLimitNotificationProps
> = ({ isSubagent, onCreateConversation, onCompactConversation }) => {
  const [isCompacting, setIsCompacting] = useState(false);

  const handleCompact = async () => {
    if (!onCompactConversation || isCompacting) return;
    setIsCompacting(true);
    try {
      await onCompactConversation();
    } finally {
      // Leave the spinner up briefly so the user sees feedback before the URL
      // changes; the parent unmounts this once the new conversation loads.
      setIsCompacting(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="mt-1">
        <ExclamationTriangleIcon className="text-chariot-text-red size-4" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="text-chariot-text-primary text-sm font-medium">
          This conversation reached the model’s context limit
        </div>
        <div className="text-chariot-text-secondary mt-1 text-sm leading-relaxed">
          {isSubagent
            ? 'This subagent gathered more information than fits in the model’s context window, so it couldn’t finish. Return to the parent conversation to continue.'
            : 'This conversation has grown too long to continue — it exceeds the model’s context window. Start a new conversation, or compact this one to keep going with a summary of what’s happened so far.'}
        </div>

        {!isSubagent && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {onCreateConversation && (
              <Button
                onClick={onCreateConversation}
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={isCompacting}
              >
                <PlusIcon className="size-4" />
                New Conversation
              </Button>
            )}
            {onCompactConversation && (
              <Button
                onClick={handleCompact}
                variant="default"
                size="sm"
                className="rounded-full"
                disabled={isCompacting}
                aria-label="Compact and continue"
              >
                <ArrowPathIcon
                  className={cn('size-4', isCompacting && SPINNER_MOTION)}
                />
                {isCompacting ? 'Compacting…' : 'Compact & Continue'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatContextLimitNotification;
